import { useState, useEffect } from 'react';
import { api } from '../lib/api.jsx';
import { AlertsTable, ALERT_SEV_LABELS, ALERT_CAT_LABELS } from '../components/AlertsTable.jsx';
import { ClientPicker } from '../components/ClientPicker.jsx';
import { useDashboard } from './dashboardContext.jsx';
import { useTableSort } from '../lib/useTableSort.jsx';
import { parseHash, viewToHash } from '../lib/router.jsx';
import { ComitBonAssignModal, useComitConfig } from '../lib/comit.jsx';

const inputS = 'bg-card border border-rule-2 rounded-lg px-2.5 py-1.5 text-fg-2 text-xs outline-none';
const btnF = (active) =>
  `rounded-lg px-3 py-1 text-[11px] cursor-pointer border transition-colors ${
    active
      ? 'bg-blue-500/15 border-blue-500 text-blue-400 font-semibold hover:bg-blue-500/25'
      : 'bg-card border-rule-2 text-muted hover:bg-card-hi hover:text-fg-2 hover:border-rule-3'
  }`;

export function AlertsPage({
  initialSeverity,
  initialCategory,
  initialClientId,
  initialSiteId,
  embedded = false,
} = {}) {
  // ClientPicker et le dropdown site tapent /api/clients (ViewClients) : sans
  // cette permission on masque le filtre plutôt que d'afficher un champ mort.
  const { can } = useDashboard();
  const canFilterByClient = can('ViewClients');
  // Assignation en lot à un bon existant : réservée à qui peut agir sur les
  // alertes, et masquée si l'intégration ComIT n'est pas configurée.
  const comit = useComitConfig();
  const canAssignBon = comit.enabled && can('ManageAlerts');
  const [assignOpen, setAssignOpen] = useState(false);
  // Alerte visée par le bouton « rattacher » d'une ligne. Null = la modale
  // porte sur la sélection multiple du haut de page.
  const [assignRow, setAssignRow] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [devices, setDevices] = useState([]);
  // Sites du client filtré — chargés à la volée, vides tant qu'aucun client
  // n'est sélectionné (le filtre site n'a pas de sens hors contexte client).
  const [sites, setSites] = useState([]);
  // Filtres initiaux : les props (arrivée depuis une carte du tableau de bord)
  // priment, sinon on relit l'URL. C'est ce qui fait survivre un F5 ou un lien
  // partagé. La version embarquée dans le tableau de bord ignore l'URL : elle
  // partage le hash « #/ » avec la page d'accueil et n'a rien à y écrire.
  const urlQ = embedded ? {} : parseHash().query;
  const numOrNull = (v) => (v === undefined || v === '' ? null : Number(v));
  // La pagination fait partie de l'état de la vue : rafraîchir en page 4 doit
  // rendre la page 4, pas revenir à la première.
  const [page, setPage] = useState(Math.max(1, numOrNull(urlQ.page) || 1));
  const [filters, setFilters] = useState({
    // `resolved` a trois états (non résolues / résolues / toutes) dont le défaut
    // est `false` : il est donc sérialisé explicitement, un simple test de
    // valeur vide confondrait « toutes » (null) et le défaut.
    resolved: urlQ.resolved === 'all' ? null : urlQ.resolved === 'true' ? true : false,
    severity: initialSeverity ?? numOrNull(urlQ.severity),
    category: initialCategory ?? numOrNull(urlQ.category),
    agentId: urlQ.agentId ?? null,
    clientId: initialClientId ?? urlQ.clientId ?? null,
    siteId: initialSiteId ?? urlQ.siteId ?? null,
    // « Server » / « Workstation », ou null pour les deux. La valeur envoyée est
    // la valeur brute stockée sur l'agent ; seule l'étiquette est en français.
    machineType: urlQ.machineType ?? null,
    search: urlQ.search ?? '',
    dateFrom: urlQ.dateFrom ?? '',
    dateTo: urlQ.dateTo ?? '',
    // Bascule de vue : coché, on ne voit QUE les alertes écartées. C'est une
    // revue à part — « qu'a-t-on décidé d'ignorer, et pourquoi ? ».
    ignoredOnly: urlQ.ignoredOnly === 'true',
  });
  const [selected, setSelected] = useState([]);
  // Le tri vit ici, pas dans AlertsTable : la table est démontée pendant chaque
  // chargement, donc un état interne serait perdu à chaque changement de filtre.
  const sortCtl = useTableSort('occurredAt', 'desc');
  const PAGE_SIZE = 30;
  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.resolved !== null) params.set('resolved', filters.resolved);
    if (filters.severity !== null) params.set('severity', filters.severity);
    if (filters.category !== null) params.set('category', filters.category);
    if (filters.agentId) params.set('agentId', filters.agentId);
    if (filters.clientId) params.set('clientId', filters.clientId);
    if (filters.siteId) params.set('siteId', filters.siteId);
    if (filters.machineType) params.set('machineType', filters.machineType);
    if (filters.search) params.set('search', filters.search);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.ignoredOnly) params.set('ignoredOnly', 'true');
    params.set('page', page);
    params.set('pageSize', PAGE_SIZE);
    const r = await api.get('/platform/alerts?' + params.toString());
    if (r) {
      setAlerts(r.items || []);
      setTotal(r.total || 0);
      setTotalPages(r.totalPages || 0);
    }
    setLoading(false);
  };
  // La liste d'appareils du dropdown suit le client/site filtré, sinon on
  // proposerait des machines qui ne peuvent plus apparaître dans la table.
  const loadDevices = async () => {
    const p = new URLSearchParams();
    if (filters.clientId) p.set('clientId', filters.clientId);
    if (filters.siteId) p.set('siteId', filters.siteId);
    if (filters.machineType) p.set('machineType', filters.machineType);
    const qs = p.toString();
    const r = await api.get('/platform/alerts/devices' + (qs ? '?' + qs : ''));
    setDevices(r || []);
  };
  useEffect(() => {
    load();
  }, [page, filters]);

  // Reflète filtres et pagination dans l'URL. `replace` plutôt qu'une écriture
  // directe du hash : cocher trois filtres ne doit pas empiler trois entrées
  // dans l'historique, sinon « Précédent » les défait une par une au lieu de
  // ramener à la page précédente.
  useEffect(() => {
    if (embedded) return;
    const q = {
      resolved: filters.resolved === null ? 'all' : filters.resolved ? 'true' : 'false',
      severity: filters.severity,
      category: filters.category,
      agentId: filters.agentId,
      clientId: filters.clientId,
      siteId: filters.siteId,
      machineType: filters.machineType,
      search: filters.search,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
      ignoredOnly: filters.ignoredOnly ? 'true' : null,
      page: page > 1 ? page : null,
    };
    // 'false' est le défaut : le garder dans l'URL n'apporte rien.
    if (q.resolved === 'false') q.resolved = null;
    const hash = viewToHash('alerts', null, null, q);
    if (window.location.hash !== hash) window.history.replaceState(null, '', hash);
  }, [filters, page, embedded]);
  useEffect(() => {
    loadDevices();
  }, [filters.clientId, filters.siteId, filters.machineType]);

  // Sites du client sélectionné (pour le dropdown site)
  useEffect(() => {
    if (!filters.clientId) {
      setSites([]);
      return;
    }
    let active = true;
    api.get('/clients/' + filters.clientId).then((r) => {
      if (active) setSites(r?.sites || []);
    });
    return () => {
      active = false;
    };
  }, [filters.clientId]);

  const setClientFilter = (clientId) => {
    // Changer de client invalide le site et l'appareil sélectionnés.
    setFilters((f) => ({ ...f, clientId: clientId || null, siteId: null, agentId: null }));
    setPage(1);
  };
  const handleIgnore = async (a) => {
    // `prompt` plutôt qu'une modale : le motif est un texte libre d'une ligne, et
    // une modale dédiée n'apporterait rien de plus ici. Le serveur refuse un
    // motif vide, donc annuler ou laisser vide ne fait rien.
    const reason = prompt(
      `Ignorer cette alerte ?\n\n${a.title}\n${a.computerName}\n\n` +
        "La condition n'est pas corrigée : elle cesse simplement d'être signalée.\n" +
        'Motif (obligatoire) :'
    );
    if (reason === null || !reason.trim()) return;
    await api.post('/platform/alerts/' + a.id + '/ignore', { reason: reason.trim() });
    load();
    loadDevices();
  };
  const handleUnignore = async (a) => {
    if (!confirm(`Remettre cette alerte sous surveillance ?\n\n${a.title}\n${a.computerName}`)) return;
    await api.post('/platform/alerts/' + a.id + '/unignore');
    load();
    loadDevices();
  };
  const handleResolve = async (id) => {
    await api.post('/platform/alerts/' + id + '/resolve');
    load();
    loadDevices();
  };
  const handleBulkResolve = async () => {
    if (selected.length === 0) return;
    await api.post('/platform/alerts/bulk-resolve', selected);
    setSelected([]);
    load();
    loadDevices();
  };
  const handleBonAssigned = () => {
    setSelected([]);
    load();
  };
  const handleCleanup = async () => {
    const r = await api.post('/platform/alerts/cleanup');
    if (r) alert(r.resolved + ' alertes nettoyees');
    load();
    loadDevices();
  };
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === alerts.length ? [] : alerts.map((a) => a.id)));
  const sevLabels = ALERT_SEV_LABELS;
  const catLabels = ALERT_CAT_LABELS;
  const Pagination = () =>
    totalPages > 1 ? (
      <div className="flex justify-center gap-1.5 mt-4">
        <button
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className={`${btnF(false)} ${page === 1 ? 'opacity-40' : ''}`}
        >
          ←
        </button>
        {Array.from(
          {
            length: Math.min(totalPages, 7),
          },
          (_, i) => {
            let p = i + 1;
            if (totalPages > 7) {
              if (page < 5) p = i + 1;
              else if (page > totalPages - 3) p = totalPages - 6 + i;
              else p = page - 3 + i;
            }
            return (
              <button key={p} onClick={() => setPage(p)} className={btnF(p === page)}>
                {p}
              </button>
            );
          }
        )}
        <button
          onClick={() => setPage(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className={`${btnF(false)} ${page >= totalPages ? 'opacity-40' : ''}`}
        >
          →
        </button>
      </div>
    ) : null;
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        {!embedded && <h1 className="text-[22px] font-bold text-fg m-0">Alertes</h1>}
        {embedded && <div className="text-sm font-semibold text-fg-2">Alertes — {total}</div>}
        <div className="flex gap-2">
          {selected.length > 0 && (
            <button
              onClick={handleBulkResolve}
              className="rounded-lg py-1.5 px-4 text-emerald-300 cursor-pointer text-xs font-semibold bg-ok/12 border border-ok/30"
            >
              {'Resoudre (' + selected.length + ')'}
            </button>
          )}
          {selected.length > 0 && canAssignBon && (
            <button
              onClick={() => setAssignOpen(true)}
              title="Rattacher les alertes selectionnees a un bon de travail ComIT deja cree"
              className="rounded-lg py-1.5 px-4 text-blue-400 cursor-pointer text-xs font-semibold bg-blue-500/12 border border-blue-500/30"
            >
              {'Assigner a un bon (' + selected.length + ')'}
            </button>
          )}
          {!embedded && (
            <button
              onClick={handleCleanup}
              className="rounded-lg py-1.5 px-4 text-orange-400 cursor-pointer text-xs font-semibold bg-orange-500/12 border border-orange-500/30"
            >
              Nettoyage auto
            </button>
          )}
        </div>
      </div>{' '}
      <div className="flex gap-2 flex-wrap mb-4 items-center">
        <input
          placeholder="Rechercher..."
          value={filters.search}
          onChange={(e) => {
            setFilters({
              ...filters,
              search: e.target.value,
            });
            setPage(1);
          }}
          className={`${inputS} w-[180px]`}
        />
        <select
          // Sans effet dans la vue « ignorées uniquement » : toute alerte
          // ignorée est marquée résolue, le serveur neutralise donc ce filtre.
          // Grisé plutôt que masqué, pour que la barre ne se réorganise pas.
          disabled={filters.ignoredOnly}
          value={filters.ignoredOnly ? 'all' : filters.resolved === null ? 'all' : filters.resolved ? 'true' : 'false'}
          onChange={(e) => {
            const v = e.target.value;
            setFilters({
              ...filters,
              resolved: v === 'all' ? null : v === 'true',
            });
            setPage(1);
          }}
          className={inputS}
        >
          <option value="all">Toutes</option>
          <option value="false">Non resolues</option>
          <option value="true">Resolues</option>
        </select>
        <select
          value={filters.severity === null ? '' : filters.severity}
          onChange={(e) => {
            setFilters({
              ...filters,
              severity: e.target.value === '' ? null : parseInt(e.target.value),
            });
            setPage(1);
          }}
          className={inputS}
        >
          <option value="">Toutes severites</option>
          {sevLabels.map((l, i) => (
            <option key={i} value={i}>
              {l}
            </option>
          ))}
        </select>
        <select
          value={filters.category === null ? '' : filters.category}
          onChange={(e) => {
            setFilters({
              ...filters,
              category: e.target.value === '' ? null : parseInt(e.target.value),
            });
            setPage(1);
          }}
          className={inputS}
        >
          <option value="">Toutes categories</option>
          {catLabels.map((l, i) => (
            <option key={i} value={i}>
              {l}
            </option>
          ))}
        </select>
        <select
          value={filters.machineType || ''}
          onChange={(e) => {
            // Changer de type invalide l'appareil sélectionné, comme changer de
            // client : il peut ne plus faire partie du jeu filtré.
            setFilters({
              ...filters,
              machineType: e.target.value || null,
              agentId: null,
            });
            setPage(1);
          }}
          className={inputS}
          title="Serveurs ou postes de travail"
        >
          <option value="">Tous les types</option>
          <option value="Server">Serveurs</option>
          <option value="Workstation">Postes de travail</option>
        </select>
        {canFilterByClient && (
          <ClientPicker
            value={filters.clientId}
            onChange={setClientFilter}
            placeholder="Tous les clients"
            className="w-[200px]"
          />
        )}
        {canFilterByClient && filters.clientId && (
          <select
            value={filters.siteId || ''}
            onChange={(e) => {
              setFilters({
                ...filters,
                siteId: e.target.value || null,
                agentId: null,
              });
              setPage(1);
            }}
            className={inputS}
          >
            <option value="">Tous les sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        )}
        <select
          value={filters.agentId || ''}
          onChange={(e) => {
            setFilters({
              ...filters,
              agentId: e.target.value || null,
            });
            setPage(1);
          }}
          className={inputS}
        >
          <option value="">Tous les appareils</option>
          {devices.map((d) => (
            <option key={d.agentId} value={d.agentId}>
              {d.computerName + ' (' + d.alertCount + ')'}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => {
            setFilters({
              ...filters,
              dateFrom: e.target.value,
            });
            setPage(1);
          }}
          className={inputS}
          title="Date debut"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => {
            setFilters({
              ...filters,
              dateTo: e.target.value,
            });
            setPage(1);
          }}
          className={inputS}
          title="Date fin"
        />
        <label
          className="inline-flex items-center gap-1.5 ml-2 text-[11px] text-muted cursor-pointer hover:text-fg-2"
          title="N'affiche que les alertes écartées volontairement. Le filtre Statut ne s'y applique pas : une alerte ignorée est toujours marquée résolue."
        >
          <input
            type="checkbox"
            checked={filters.ignoredOnly}
            onChange={(e) => {
              setFilters({ ...filters, ignoredOnly: e.target.checked });
              setPage(1);
            }}
          />
          Ignorées uniquement
        </label>
        <span className="text-[11px] text-muted-2 ml-2">{total + ' alerte(s)'}</span>
      </div>
      {
        // Table
        loading ? (
          <div className="text-muted-2 p-10">Chargement...</div>
        ) : alerts.length === 0 ? (
          <div className="text-muted-2 p-10 text-center">Aucune alerte</div>
        ) : (
          <div className="bg-card border border-rule rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <AlertsTable
                alerts={alerts}
                selected={selected}
                toggleSelect={toggleSelect}
                toggleAll={toggleAll}
                handleResolve={handleResolve}
                onIgnore={handleIgnore}
                onUnignore={handleUnignore}
                onAssignBon={
                  canAssignBon
                    ? (a) => {
                        setAssignRow(a);
                        setAssignOpen(true);
                      }
                    : null
                }
                sortCtl={sortCtl}
                showSelect
                showActions
                onBonChanged={load}
              />
            </div>
            <Pagination />
          </div>
        )
      }
      {assignOpen && (
        <ComitBonAssignModal
          cible={{ alertIds: assignRow ? [assignRow.id] : selected }}
          resume={
            assignRow
              ? assignRow.title + ' — ' + assignRow.computerName
              : selected.length + ' alerte(s) selectionnee(s)'
          }
          onClose={() => {
            setAssignOpen(false);
            setAssignRow(null);
          }}
          onDone={() => {
            setAssignRow(null);
            handleBonAssigned();
          }}
        />
      )}
    </div>
  );
}
